import { FeatureFlag } from 'constant/featureFlag'
import getEnv from 'utilities/getEnv'
import { isNotNull } from 'utilities/typeUtility/typeUtility'

/**
 * Maps the string representation of a feature flag to its `FeatureFlag` enum member/key.
 * - E.g. `"custom_uom"` is mapped to `FeatureFlag.CustomUOM`.
 * - Case-insensitive.
 * @param featureFlagAsString The string representation of a feature flag.
 * @returns The matching `FeatureFlag` enum member/key of the provided string or `null` if no match is found.
 */
function mapToFeatureFlag(featureFlagAsString: string): FeatureFlag | null {
    return (
        Object.values(FeatureFlag).find(
            (value) =>
                value.toLowerCase() === featureFlagAsString.toLowerCase(),
        ) ?? null
    )
}

/**
 * Returns a set (i.e. unique list) of active/enabled feature flags from both PostHog and dev config.
 * @param postHogFeatureFlags List of enabled feature flags from PostHog. Has to be passed in since the
 * useActiveFeatureFlags hook from PostHog cannot be called from here.
 * @returns Set of active feature flags.
 */
export function getActiveFeatureFlags(
    postHogFeatureFlags: string[] | undefined,
): Set<FeatureFlag> {
    // Feature flags enabled for development.
    const developmentFeatureFlags: FeatureFlag[] = (
        getEnv('FEATURE_FLAGS') ?? []
    )
        .map(mapToFeatureFlag)
        .filter(isNotNull)
    // Map enabled (a.k.a. active) PostHog feature flags.
    const postHogFeatureFlagsMapped: FeatureFlag[] = (postHogFeatureFlags ?? [])
        .map(mapToFeatureFlag)
        .filter(isNotNull)

    // Build a set of enabled feature flags.
    return new Set<FeatureFlag>([
        ...developmentFeatureFlags,
        ...postHogFeatureFlagsMapped,
    ])
}
